import type { Dispatch, FC, SetStateAction } from 'react';

import { useCms } from '@eneco/cms';
import AddressFinder from '@eneco/components/AddressFinder/AddressFinder';
import {
  Bucket,
  Grid,
  InputEmail,
  InputNumber,
  InputTelephone,
  InputText,
  RadioButton,
  RadioGroup,
} from '@eneco/sparky';
import { Controller, useFormContext } from 'react-hook-form';

import type { FormValues } from './types';
import { toAddressFinderCustomLabels } from '../address-finder-custom-labels';
import type { CorrectionInvoiceFormContent } from '../correction-invoice-form-content.types';

type Props = {
  content: CorrectionInvoiceFormContent;
  isSuffixRequired: boolean;
  setIsSuffixRequired: Dispatch<SetStateAction<boolean>>;
};

/**
 * Mirrors the legacy `components/PersonalDetails.tsx`. A salutation option has a `value` only on Sitecore,
 * equal to its label, which Sanity's options fall back to so the submitted answer stays the same.
 */
export const PersonalDetails: FC<Props> = ({ content, isSuffixRequired, setIsSuffixRequired }) => {
  const { adapter } = useCms();
  const {
    formFieldsPersonal_title,
    salutationFormField,
    customerIdFormField,
    emailAddressFormField,
    firstNameFormField,
    initialsFormField,
    phoneNumberFormField,
    surnameFormField,
    surnamePrepositionFormField,
  } = content;

  const {
    register,
    formState: { errors },
    control,
  } = useFormContext<FormValues>();
  return (
    <Bucket title={formFieldsPersonal_title ?? ''}>
      <Bucket.Content>
        <Grid columns="2" gap="6">
          <InputNumber
            label={customerIdFormField?.label ?? ''}
            error={errors?.customerId?.message}
            {...register('customerId')}
            formatOptions={{ useGrouping: false }}
          />
          <Grid.Item gridColumn="1/-1">
            <Controller
              control={control}
              name="salutation"
              render={({ field: { onChange, value, name } }) => (
                <RadioGroup
                  name={name}
                  label={salutationFormField?.label ?? ''}
                  value={value}
                  onValueChange={onChange}
                  error={errors.salutation?.message}>
                  {salutationFormField?.options?.map(option => {
                    const optionValue = option.value ?? option.label ?? '';
                    return (
                      <RadioButton value={optionValue} key={optionValue}>
                        {option.label ?? ''}
                      </RadioButton>
                    );
                  })}
                </RadioGroup>
              )}
            />
          </Grid.Item>
          <InputText
            label={initialsFormField?.label ?? ''}
            error={errors?.initials?.message}
            {...register('initials')}
          />
          <InputText
            label={firstNameFormField?.label ?? ''}
            error={errors?.firstName?.message}
            {...register('firstName')}
          />

          <InputText
            label={surnamePrepositionFormField?.label ?? ''}
            error={errors?.surnamePreposition?.message}
            {...register('surnamePreposition')}
            isOptional
          />
          <InputText label={surnameFormField?.label ?? ''} error={errors?.surname?.message} {...register('surname')} />
          <Grid.Item gridColumn="1/-1">
            <AddressFinder
              isSuffixRequired={isSuffixRequired}
              setIsSuffixRequired={setIsSuffixRequired}
              customLabels={toAddressFinderCustomLabels(adapter, content)}
            />
          </Grid.Item>
          <InputTelephone
            label={phoneNumberFormField?.label ?? ''}
            error={errors?.phoneNumber?.message}
            autoComplete="tel"
            {...register('phoneNumber')}
          />
          <InputEmail
            label={emailAddressFormField?.label ?? ''}
            error={errors?.emailAddress?.message}
            autoComplete="email"
            {...register('emailAddress')}
          />
        </Grid>
      </Bucket.Content>
    </Bucket>
  );
};
